<script setup lang="ts">
import { onMounted, ref } from "vue";
import Details from "@/components/settings/Details.vue";
import AsideModal from "@/components/aside/AsideModal.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import { useI18n } from "vue-i18n";

const step = ref("settings");
const isShowFinishedModal = ref(false);
const router = useRouter();
const route = useRoute();

const store = useStore();
const { t } = useI18n();

const sections = ref([
  {
    title: t("settings.sections.details"),
    step: "details",
  },
  {
    title: t("settings.sections.documentation"),
    step: "docs",
  },
]);

const setSettingsStep = (sectionsStep: string) => {
  if (sectionsStep === "docs") {
    window.open("https://moioperator.ru/preoffer", "_blank");
    return;
  }
  step.value = sectionsStep;
};

const submit = () => {
  isShowFinishedModal.value = true;
  store.dispatch("updateAsideVisibility", true);
};

const close = () => {
  isShowFinishedModal.value = false;
  store.dispatch("updateAsideVisibility", false);
  step.value = "settings";
};

const clearData = async () => {
  store.dispatch("auth/logout");
};

const goToFeedback = () => {
  router.push("/feedback");
};

onMounted(() => {
  store.dispatch("updateAsideVisibility", false);
  if (route.query.details) {
    setSettingsStep("details");
  }
});
</script>

<template>
  <div class="settings">
    <div v-if="step === 'settings'" class="settings__main">
      <h1 class="settings__title h1 color-text-primary">
        {{ t("settings.title") }}
      </h1>
      <div class="settings__sections">
        <div
          class="settings__section h4 color-text-primary"
          @click="setSettingsStep('docs')"
        >
          {{ sections[1].title }}
          <BaseFigureForIcon
            icon="more"
            color="secondary"
            size="small"
            is-button
          />
        </div>
        <div
          class="settings__section h4 color-text-primary"
          @click="goToFeedback"
        >
          {{ t("settings.sections.feedback") }}
          <BaseFigureForIcon
            icon="more"
            color="secondary"
            size="small"
            is-button
          />
        </div>
      </div>
      <BaseButton
        @click="clearData()"
        :title="t('settings.logout')"
        size="large"
        color="transparent-primary"
        class="logout-btn"
      />
      <span class="settings__version caption-medium"> ver. 1.0 </span>
    </div>
    <Details
      v-else-if="step === 'details'"
      @back="step = 'settings'"
      @submit="submit"
    />
    <div class="finish-modal__wrap" v-if="isShowFinishedModal">
      <AsideModal
        :is-show="isShowFinishedModal"
        :is-white="false"
        :full-height="true"
        :is-hide-close="true"
        :close-out-side="true"
        @close="close"
        class="finish-modal"
      >
        <h1 class="h1 color-white finish-modal__title">
          {{ t("settings.detailsSentLine1") }} <br />
          {{ t("settings.detailsSentLine2") }} <br />
          {{ t("settings.detailsSentLine3") }}
        </h1>
        <div class="finish-modal__icon">
          <BaseIcon name="check-success" folder="display" class="icon" />
        </div>
        <BaseButton
          class="finish-modal__btn"
          color="white"
          title="OK"
          @click="close"
        />
      </AsideModal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/main";

.settings {
  display: flex;
  width: 100%;
  max-width: 720px;
  margin: 45px 0;

  @media (max-width: 960px) {
    margin: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
  }

  &__main {
    width: 100%;
  }

  &__title {
    display: none;

    @media (max-width: 960px) {
      display: flex;
      align-items: flex-start;
      margin: 12px 0 31px 24px;
    }
  }

  &__sections {
    display: flex;
    flex-direction: column;
    gap: 13px;
    width: 100%;
    margin-bottom: 22px;

    @media (max-width: 960px) {
      padding: 0 24px;
      gap: 11px;
      margin-bottom: 0;
    }
  }

  &__section {
    display: flex;
    padding: 38px 38px 38px 24px;
    background-color: var(--color-white);
    border-radius: 32px;
    justify-content: space-between;
    cursor: pointer;

    @media (max-width: 960px) {
      padding: 24px 23px 24px 20px;
      border-radius: 24px;
    }
  }

  &__img {
    width: 24px;
    height: 24px;
    background-color: var(--color-secondary);
    padding: 4px;
    transform: rotate(-90deg);
    border-radius: 24px;
  }

  &__version {
    color: var(--color-text-input);
    margin-top: 22px;
    @media (max-width: 960px) {
      position: absolute;
      bottom: 125px;
      display: flex;
      left: 24px;
    }
  }
}

.finish-modal {
  max-width: 480px;
  max-height: 480px;
  height: 100%;
  width: 100%;
  position: fixed;
  z-index: 11;
  left: 50%;
  right: 50%;
  transform: translate(-50%, -50%);
  border-radius: 32px;
  padding: 32px;
  background: var(--gradient-main);

  @media (max-width: 768px) {
    max-height: 100%;
    height: 100%;
    width: 100%;
    max-width: 100%;
    left: 0;
    right: 0;
    transform: none;
    border-radius: 0;
    padding: 0;
  }

  &__wrap {
    z-index: 100;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 768px) {
      align-items: flex-end;
    }
  }

  &__title {
    @media (max-width: 768px) {
      display: flex;
      justify-content: flex-start;
      padding: 48px 24px;
    }
  }

  &__icon {
    position: absolute;
    right: 24px;
    bottom: 130px;
    color: var(--color-white);
    .icon {
      height: 120px;
      width: 156px;
    }
  }

  &__btn {
    position: absolute;
    bottom: 10px;
    left: 50%;
    right: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
  }
}

.logout-btn {
  display: none;
  @media (max-width: 960px) {
    position: absolute;
    bottom: 150px;
    display: flex;
    left: -16px;
  }
}
</style>
